// src/pages/ProjectsPage.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { projectsAPI, customersAPI } from '../services/api';

const ProjectsPage = () => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState(null);
  const [formData, setFormData] = useState({
    customer_id: '',
    title: '',
    description: '',
    status: 'planned'
  });

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        console.log('Lade Projekte und Kunden...');
        const [projectsResponse, customersResponse] = await Promise.all([
          projectsAPI.getAll(),
          customersAPI.getAll()
        ]);
        
        console.log(`${projectsResponse.data.projects.length} Projekte geladen`);
        console.log(`${customersResponse.data.customers.length} Kunden geladen`);
        
        setProjects(projectsResponse.data.projects);
        setCustomers(customersResponse.data.customers);
        setError('');
      } catch (err) {
        console.error('Fehler beim Laden der Daten:', err);
        
        // Prüfen, ob es ein Authentifizierungsfehler ist
        if (err.response && err.response.status === 401) {
          console.log('Nicht authentifiziert, leite zur Login-Seite um');
          navigate('/login');
          return;
        }
        
        setError('Fehler beim Laden der Daten');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [navigate]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      console.log('Erstelle neues Projekt:', formData);
      await projectsAPI.create(formData);
      console.log('Projekt erfolgreich erstellt');
      
      // Projekte neu laden
      const response = await projectsAPI.getAll();
      setProjects(response.data.projects);
      
      // Formular zurücksetzen
      setShowForm(false);
      setFormData({
        customer_id: '',
        title: '',
        description: '',
        status: 'planned'
      });
      
      setError('');
    } catch (err) {
      console.error('Fehler beim Erstellen des Projekts:', err);
      
      // Prüfen, ob es ein Authentifizierungsfehler ist
      if (err.response && err.response.status === 401) {
        console.log('Nicht authentifiziert, leite zur Login-Seite um');
        navigate('/login');
        return;
      }
      
      setError('Fehler beim Erstellen des Projekts');
    }
  };

  // Funktionen für die Navigation
  const handleNavigateToDetail = (projectId) => {
    // Prüfen, ob ein Token vorhanden ist
    const token = localStorage.getItem('token');
    if (!token) {
      console.warn('Kein Token vorhanden - leite zur Login-Seite weiter');
      navigate('/login');
      return;
    }
    navigate(`/projekte/${projectId}`);
  };

  const handleNavigateToEdit = (projectId) => {
    // Prüfen, ob ein Token vorhanden ist
    const token = localStorage.getItem('token');
    if (!token) {
      console.warn('Kein Token vorhanden - leite zur Login-Seite weiter');
      navigate('/login');
      return;
    }
    navigate(`/projekte/${projectId}/edit`);
  };

  // Funktion zum Löschen eines Projekts
  const handleDeleteProject = async (projectId) => {
    try {
      console.log('Versuche Projekt zu löschen, ID:', projectId);
      await projectsAPI.delete(projectId);
      console.log('Projekt erfolgreich gelöscht');
      
      // Projekte neu laden
      const response = await projectsAPI.getAll();
      setProjects(response.data.projects);
      
      // Dialog schließen
      setDeleteConfirmation(null);
      setError('');
    } catch (err) {
      console.error('Fehler beim Löschen des Projekts:', err);
      
      // Prüfen, ob es ein Authentifizierungsfehler ist
      if (err.response && err.response.status === 401) {
        console.log('Nicht authentifiziert, leite zur Login-Seite um');
        navigate('/login');
        return;
      }
      
      setError('Fehler beim Löschen des Projekts');
    }
  };

  // Status in lesbaren Text umwandeln
  const getStatusText = (status) => {
    const statusMap = {
      planned: 'Geplant',
      in_progress: 'In Bearbeitung',
      completed: 'Abgeschlossen',
      cancelled: 'Storniert'
    };
    return statusMap[status] || status;
  };

  // Status-Farbe bestimmen
  const getStatusColor = (status) => {
    const colorMap = {
      planned: 'bg-blue-100 text-blue-800',
      in_progress: 'bg-yellow-100 text-yellow-800',
      completed: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800'
    };
    return colorMap[status] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return <div className="text-center py-6 sm:py-10">Lade Projektdaten...</div>;
  }

  return (
    <div className="p-4 sm:p-0">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-4 sm:mb-6 gap-3 sm:gap-0">
        <h1 className="text-xl sm:text-2xl font-semibold">Projektverwaltung</h1>
        <button
          onClick={() => setShowForm(!showForm)}
          className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded w-full sm:w-auto"
        >
          {showForm ? 'Abbrechen' : 'Neues Projekt'}
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border-l-4 border-red-500 p-3 sm:p-4 mb-4 sm:mb-6">
          <p className="text-sm sm:text-base text-red-700">{error}</p>
        </div>
      )}

      {showForm && (
        <div className="bg-white p-4 sm:p-6 rounded-lg shadow-md mb-4 sm:mb-6">
          <h2 className="text-lg sm:text-xl font-medium mb-3 sm:mb-4">Neues Projekt</h2>
          <form onSubmit={handleSubmit}>
            <div className="mb-3 sm:mb-4">
              <label className="block text-sm sm:text-base text-gray-700 mb-1 sm:mb-2">Kunde *</label>
              <select
                name="customer_id"
                value={formData.customer_id}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="">-- Kunden auswählen --</option>
                {customers.map((customer) => (
                  <option key={customer.id} value={customer.id}>
                    {customer.company_name ? `${customer.company_name} (${customer.first_name} ${customer.last_name})` : `${customer.first_name} ${customer.last_name}`}
                  </option>
                ))}
              </select>
            </div>
            <div className="mb-3 sm:mb-4">
              <label className="block text-sm sm:text-base text-gray-700 mb-1 sm:mb-2">Projekttitel *</label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <div className="mb-3 sm:mb-4">
              <label className="block text-sm sm:text-base text-gray-700 mb-1 sm:mb-2">Beschreibung</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                rows="3"
              />
            </div>
            <div className="mb-3 sm:mb-4">
              <label className="block text-sm sm:text-base text-gray-700 mb-1 sm:mb-2">Status</label>
              <select
                name="status"
                value={formData.status}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="planned">Geplant</option>
                <option value="in_progress">In Bearbeitung</option>
                <option value="completed">Abgeschlossen</option>
                <option value="cancelled">Storniert</option>
              </select>
            </div>
            <div className="flex justify-end">
              <button
                type="submit"
                className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded w-full sm:w-auto"
              >
                Projekt speichern
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Löschbestätigung Dialog */}
      {deleteConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg max-w-md w-full">
            <h3 className="text-lg font-medium mb-4">Projekt löschen</h3>
            <p className="mb-6">
              Möchten Sie das Projekt "{deleteConfirmation.title}" wirklich löschen?
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setDeleteConfirmation(null)}
                className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-100"
              >
                Abbrechen
              </button>
              <button
                onClick={() => handleDeleteProject(deleteConfirmation.id)}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
              >
                Löschen
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white p-4 sm:p-6 rounded-lg shadow-md">
        <h2 className="text-lg sm:text-xl font-medium mb-3 sm:mb-4">Projektliste</h2>
        
        {projects.length === 0 ? (
          <p className="text-sm sm:text-base text-gray-500">Keine Projekte vorhanden</p>
        ) : (
          <div className="overflow-x-auto -mx-4 sm:mx-0">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Titel</th>
                  <th className="hidden sm:table-cell px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Kunde</th>
                  <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="hidden md:table-cell px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Erstellt am</th>
                  <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Aktionen</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {projects.map((project) => (
                  <tr key={project.id} className="hover:bg-gray-50">
                    <td className="px-3 sm:px-6 py-3 sm:py-4">
                      <div className="text-sm font-medium text-gray-900">{project.title}</div>
                      <div className="text-xs sm:text-sm text-gray-500 line-clamp-1 sm:line-clamp-2">
                        {project.description && project.description.substring(0, 50) + (project.description.length > 50 ? '...' : '')}
                      </div>
                      {/* Mobile-only customer info */}
                      <div className="sm:hidden text-xs text-gray-700 mt-1">
                        {project.company_name || `${project.first_name} ${project.last_name}`}
                      </div>
                    </td>
                    <td className="hidden sm:table-cell px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{project.company_name || `${project.first_name} ${project.last_name}`}</div>
                    </td>
                    <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 inline-flex text-xs leading-4 font-semibold rounded-full ${getStatusColor(project.status)}`}>
                        {getStatusText(project.status)}
                      </span>
                    </td>
                    <td className="hidden md:table-cell px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(project.created_at).toLocaleDateString('de-DE')}
                    </td>
                    <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3">
                        <button 
                          onClick={() => handleNavigateToDetail(project.id)}
                          className="text-primary-600 hover:text-primary-900"
                        >
                          Details
                        </button>
                        <button
                          onClick={() => handleNavigateToEdit(project.id)}
                          className="text-primary-600 hover:text-primary-900"
                        >
                          Bearbeiten
                        </button>
                        <button
                          onClick={() => setDeleteConfirmation(project)}
                          className="text-red-600 hover:text-red-900"
                        >
                          Löschen
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProjectsPage;
